/* Not used yet */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { borrowingsApi } from "@api";

import { RootState } from ".";

export type BorrowingableType =
    | "App\\Models\\VehicleItem"
    | "App\\Models\\Product"
    | "App\\Models\\InstrumentItem";

type BorrowingsByItemEntry = {
    borrowings: any[];
    returnedItems: any[];
    lastFetched?: string;
};

interface BorrowingsState {
    byItem: Record<string, BorrowingsByItemEntry>;
    loadingByType: {
        vehicles: number[];
        instruments: number[];
        products: number[];
    };
    errorByItem: Record<string, string | null>;
}

type FetchBorrowingsArgs = {
    borrowingableType: BorrowingableType;
    borrowingableId: number | string;
    limit?: number;
    since?: string;
};

const buildItemKey = (borrowingableType: string, borrowingableId: number | string) =>
    `${borrowingableType}:${borrowingableId}`;

const getLoadingBucket = (borrowingableType: BorrowingableType) => {
    switch (borrowingableType) {
        case "App\\Models\\VehicleItem":
            return "vehicles";
        case "App\\Models\\InstrumentItem":
            return "instruments";
        case "App\\Models\\Product":
        default:
            return "products";
    }
};

const normalizeId = (id: number | string) => Number(id);

const initialState: BorrowingsState = {
    byItem: {},
    loadingByType: {
        vehicles: [],
        instruments: [],
        products: [],
    },
    errorByItem: {},
};

export const fetchBorrowingsByItem = createAsyncThunk(
    "borrowings/fetchBorrowingsByItem",
    async (args: FetchBorrowingsArgs, { rejectWithValue }) => {
        try {
            const response = await borrowingsApi.getBorrowingsByItem(args);
            const payload = response.data?.data ?? response.data;
            return {
                key: buildItemKey(args.borrowingableType, args.borrowingableId),
                data: payload,
            };
        } catch (err: any) {
            return rejectWithValue(err.response?.data ?? err.message);
        }
    }
);

const borrowingsSlice = createSlice({
    name: "borrowings",
    initialState,
    reducers: {
        clearBorrowingsForItem: (state, action: { payload: FetchBorrowingsArgs }) => {
            const key = buildItemKey(action.payload.borrowingableType, action.payload.borrowingableId);
            delete state.byItem[key];
            const bucket = getLoadingBucket(action.payload.borrowingableType);
            const normalizedId = normalizeId(action.payload.borrowingableId);
            state.loadingByType[bucket] = state.loadingByType[bucket].filter(
                (id) => id !== normalizedId
            );
            delete state.errorByItem[key];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchBorrowingsByItem.pending, (state, action) => {
            const { borrowingableType, borrowingableId } = action.meta.arg;
            const key = buildItemKey(borrowingableType, borrowingableId);
            const bucket = getLoadingBucket(borrowingableType);
            const normalizedId = normalizeId(borrowingableId);
            if (!Number.isNaN(normalizedId) && !state.loadingByType[bucket].includes(normalizedId)) {
                state.loadingByType[bucket].push(normalizedId);
            }
            state.errorByItem[key] = null;
        });
        builder.addCase(fetchBorrowingsByItem.fulfilled, (state, action) => {
            const { key, data } = action.payload as {
                key: string;
                data: { borrowings?: any[]; returnedItems?: any[] };
            };
            state.byItem[key] = {
                borrowings: data.borrowings ?? [],
                returnedItems: data.returnedItems ?? [],
                lastFetched: new Date().toISOString(),
            };
            const [typeValue, idValue] = key.split(":");
            const bucket = getLoadingBucket(typeValue as BorrowingableType);
            const normalizedId = normalizeId(idValue);
            state.loadingByType[bucket] = state.loadingByType[bucket].filter(
                (id) => id !== normalizedId
            );
        });
        builder.addCase(fetchBorrowingsByItem.rejected, (state, action) => {
            const { borrowingableType, borrowingableId } = action.meta.arg as FetchBorrowingsArgs;
            const key = buildItemKey(borrowingableType, borrowingableId);
            const bucket = getLoadingBucket(borrowingableType);
            const normalizedId = normalizeId(borrowingableId);
            state.loadingByType[bucket] = state.loadingByType[bucket].filter(
                (id) => id !== normalizedId
            );
            state.errorByItem[key] =
                (action.payload as string) || action.error?.message || "Failed to fetch borrowings";
        });
    },
});

export const selectBorrowingsState = (state: RootState) => state.borrowings;
export const selectBorrowingsByItem = (state: RootState, key: string) =>
    state.borrowings.byItem[key];
export const selectBorrowingsLoadingByType = (state: RootState) =>
    state.borrowings.loadingByType;
export const selectBorrowingsError = (state: RootState, key: string) =>
    state.borrowings.errorByItem[key];

export const { clearBorrowingsForItem } = borrowingsSlice.actions;

export default borrowingsSlice.reducer;
